import React from 'react';
import { pricingPlans } from '../data/mockData';
import { Button } from './ui/button';
import { Check } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const PricingCard = ({ plan, onOpenContactForm, delay }) => {
  const card = useScrollAnimation({ threshold: 0.15 });

  return (
    <div
      ref={card.ref}
      data-testid={`pricing-card-${plan.id}`}
      className={`group relative p-7 flex flex-col transition-all duration-200 scroll-fade-up scroll-delay-${delay} ${
        plan.recommended
          ? 'bg-white border-2 border-[#FF1010] shadow-[8px_8px_0px_#FF1010] -translate-y-2'
          : 'bg-white border border-[#E5E7EB] brutalist-hover'
      } ${card.isVisible ? 'scroll-visible' : ''}`}
    >
      {plan.recommended && (
        <div className="absolute -top-4 left-0 right-0 flex justify-center">
          <span className="bg-[#FF1010] text-white text-[10px] font-black uppercase tracking-widest px-4 py-1.5">
            Most Popular
          </span>
        </div>
      )}

      <div className="mb-5">
        <h3 className="text-base font-black tracking-tighter text-[#111827] uppercase mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {plan.name}
        </h3>
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-black tracking-tighter text-[#111827]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {plan.price}
          </span>
        </div>
        <p className="text-xs mt-0.5 text-[#9CA3AF]">{plan.federal}</p>
      </div>

      <ul className="space-y-2.5 flex-1 mb-6">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.recommended ? 'text-[#FF1010]' : 'text-[#111827]'}`} strokeWidth={3} />
            <span className="text-sm text-[#4B5563]">{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto">
        <p className="text-[10px] text-center mb-3 text-[#9CA3AF]">{plan.note}</p>
        <Button
          data-testid={`pricing-cta-${plan.id}`}
          onClick={onOpenContactForm}
          className={`w-full font-bold h-11 rounded-none uppercase tracking-wider text-sm ${
            plan.recommended
              ? 'bg-[#FF1010] text-white hover:bg-[#CC0000] btn-brutal'
              : 'bg-[#111827] text-white hover:bg-[#FF1010] btn-brutal'
          }`}
        >
          {plan.buttonText}
        </Button>
      </div>
    </div>
  );
};

const PricingSection = ({ onOpenContactForm }) => {
  const header = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 bg-[#F9FAFB] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <div ref={header.ref} className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-up ${header.isVisible ? 'scroll-visible' : ''}`}>
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF1010] block mb-3">Pricing</span>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#111827] mb-4 uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
            No Hidden Fees. Ever.
          </h2>
          <p className="text-base text-[#4B5563] leading-relaxed">
            Pick a plan. Pay the listed price. Get trademark protection. That simple.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
          {pricingPlans.map((plan, i) => (
            <PricingCard key={plan.id} plan={plan} onOpenContactForm={onOpenContactForm} delay={i + 1} />
          ))}
        </div>

        <p className="text-center text-xs text-[#9CA3AF] mt-8 max-w-2xl mx-auto">
          All packages exclude USPTO federal filing fees ($250-$350 per class), paid directly to the government.
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
